import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2Icon, TrendingUpIcon, AwardIcon, ChevronRightIcon, ChevronDownIcon, UnlockIcon, AlertCircleIcon, InfoIcon, ShieldCheckIcon } from 'lucide-react';
import { BRAND } from '../../../shared/config/brand';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { mockFacilities } from '../mocks/financingData';
import { BusinessTiers } from '../components/BusinessTiers';
type FinancingState = 'ineligible' | 'pre-approved' | 'active-repayment' | 'top-up-unlocked';
export function Financing() {
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<FinancingState>('top-up-unlocked');
  const [borrowAmount, setBorrowAmount] = useState<number>(130000);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  const [pastFacilitiesOpen, setPastFacilitiesOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState<number>(170000);
  // The earned loyalty discount is the core value of the top-up, so the rate
  // breakdown is expanded by default here (pre-approved stays collapsed).
  const [topUpRateOpen, setTopUpRateOpen] = useState(true);
  const MAX_AMOUNT = 150000;
  const MIN_AMOUNT = 10000;
  const HOLDBACK_PERCENT = 10;
  // Pre-approved murabaha pricing: first facility, so the base profit rate
  // applies with no loyalty discount yet (loyalty discounts begin on facility #2).
  const BASE_PROFIT_RATE = 1.15;
  const PROFIT_RATE = BASE_PROFIT_RATE; // ×1.15 — no discount for a first facility
  const fixedProfit = borrowAmount * (PROFIT_RATE - 1); // amount × 0.15
  const totalPayable = borrowAmount + fixedProfit; // amount + profit
  const estimatedDailyDeduction = borrowAmount * 0.00385; // ~MYR 500 at 130k (10% of daily settlement)
  const formatMYR = (amount: number) => formatCurrencyAmount(amount, {
    currency: 'MYR'
  });
  // Estimated daily deduction is an estimate — round it to a sensible figure so
  // the "~" and the precision don't contradict each other (e.g. "~MYR 650/day").
  const formatRoughDaily = (v: number) => `~${formatMYR(Math.round(v / 50) * 50)}/day`;
  // Single-active-facility model: the merchant has exactly one live facility;
  // everything else is history. An "In arrears" condition is a status on the
  // active facility, never a separate concurrent row, so it's excluded here.
  const currentFacility = mockFacilities.find(f => f.status === 'Active');
  // Past facilities are only ever fully-closed (Paid off). A facility that is
  // still repaying is the single active one, never "past".
  const pastFacilities = mockFacilities.filter(f => f.status === 'Paid off');
  // The merchant's current loyalty tier (drives the Business Tiers section).
  const currentTier = 'Gold';
  // TOP-UP UNLOCKED: re-modelled murabaha offer for a repeat borrower. The
  // earned Gold loyalty discount is now applied live, and the new facility
  // settles + replaces the current one (single-facility model).
  const TOPUP_MAX = 170000;
  const TOPUP_BASE_RATE = 1.15;
  const TOPUP_DISCOUNT = 0.06;
  const TOPUP_RATE = TOPUP_BASE_RATE - TOPUP_DISCOUNT; // ×1.09
  // Current facility settlement (with ibra' rebate) — same maths as the
  // facility detail early-settlement panel, derived from the live facility.
  const activeFixedProfit = currentFacility ? currentFacility.totalRepayment - currentFacility.principal : 0;
  const activeIbra = currentFacility ? Math.round(activeFixedProfit * (1 - currentFacility.repaidPercent / 100)) : 0;
  const currentSettlement = currentFacility ? currentFacility.outstanding - activeIbra : 0;
  // Slider floor must be at least the current-facility settlement so that
  // "net cash you receive" (new amount − settlement) can never go negative.
  const TOPUP_MIN = Math.max(50000, currentSettlement);
  const topUpNewProfit = topUpAmount * (TOPUP_RATE - 1); // amount × 0.09
  const topUpTotalPayable = topUpAmount + topUpNewProfit;
  const topUpNetCash = topUpAmount - currentSettlement;
  const topUpDailyDeduction = topUpAmount * 0.00385; // ~10% of daily settlement
  return <div className="max-w-[720px] mx-auto space-y-8 pb-12" data-id="element-4710" data-mp="1v7xjbf6qylls">
      {/* Dev State Toggle (for prototype demonstration) */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-4711" data-mp="1r98199arkfvt">
        <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-4712" data-mp="yi6k52niga9n">
          <InfoIcon size={16} data-id="element-4713" data-mp="yk9hy8uwn7ox" />
          <span data-id="element-4714" data-mp="1chpqpr0obdju">Prototype State:</span>
        </div>
        <div className="flex gap-2" data-id="element-4715" data-mp="1cjitgv861dmk">
          {(['ineligible', 'pre-approved', 'active-repayment', 'top-up-unlocked'] as FinancingState[]).map(s => <button key={s} onClick={() => setViewState(s)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewState === s ? 'bg-[var(--brand-primary)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4716" data-mp="2iyjj6j6lkzkp">
              {s.replace('-', ' ').toUpperCase()}
            </button>)}
        </div>
      </div>

      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-id="element-4717" data-mp="2mffqbxzkfihq">
        <div data-id="element-4718" data-mp="3a509agvpyfr0">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-4719" data-mp="3a6l2o36arsna">Financing</h1>
          {viewState === 'ineligible' && <p className="text-gray-700 mt-2 font-medium" data-id="element-4720" data-mp="3f3bl2x5kr0bv">
              Shariah-compliant business financing.
            </p>}
          <p className="text-gray-500 mt-1" data-id="element-4721" data-mp="30dw2i4g76179">
            Flexible, data-driven capital to grow your business.
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium ring-1 ring-emerald-100" data-id="element-4722" data-mp="3t3w2bovuco39">
            <CheckCircle2Icon size={13} data-id="element-4723" data-mp="1rjjb3l7omym" />
            Shariah-compliant · Shariah Advisory Council
          </span>
        </div>
      </div>

      {/* BLOCK 1 & 2: Eligibility progress (ineligible state) */}
      {viewState === 'ineligible' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8" data-id="element-4724" data-mp="1mhpxfqhe0ag">
          <div className="max-w-2xl" data-id="element-4725" data-mp="3trv2e4fswpi4">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4726" data-mp="3tqs9jqyx4czk">
              Unlock {BRAND.name} Financing
            </h2>
            <div className="flex items-center gap-2 mb-6" data-id="element-4727" data-mp="3ou1yxj5vu9lj">
              <AlertCircleIcon size={18} className="text-amber-500" data-id="element-4728" data-mp="3k0qpda9orepx" />
              <p className="text-gray-700 font-medium" data-id="element-4729" data-mp="3f1w6y9fmrs6c">
                You're not eligible yet — here's what's left.
              </p>
            </div>
            <p className="text-gray-600 mb-8" data-id="element-4730" data-mp="3a19jarqiuytf">
              Keep processing with us to build your business profile and access
              this Shariah-compliant, fixed-profit financing facility.
            </p>

            {/* Progress meters */}
            <div className="space-y-6" data-id="element-4731" data-mp="1gley3nzcb8g2">
              <div data-id="element-4732" data-mp="1gkc95llkvg2s">
                <div className="flex justify-between text-sm mb-2" data-id="element-4733" data-mp="1gmyc17xix7eo">
                  <span className="font-medium text-gray-700" data-id="element-4734" data-mp="1li5d07zerxzw">
                    Time on platform
                  </span>
                  <span className="text-amber-700 font-medium" data-id="element-4735" data-mp="1pjh64nhhr1cu">
                    4 / 6 months
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4736" data-mp="xux9nvljiz0i">
                  <div className="h-full bg-amber-400 w-[66%]" data-id="element-4737" data-mp="11vfzjk3vhv02" />
                </div>
                <p className="text-xs text-gray-500 mt-1.5" data-id="element-4738" data-mp="16usqafxv7m7x">
                  2 more months of processing needed
                </p>
              </div>
              <div data-id="element-4739" data-mp="1bmamyrsuv0n3">
                <div className="flex justify-between text-sm mb-2" data-id="element-4740" data-mp="1bnlsox5v2igu">
                  <span className="font-medium text-gray-700" data-id="element-4741" data-mp="e5dcxjiq9h8u">
                    Monthly volume
                  </span>
                  <span className="text-amber-700 font-medium" data-id="element-4742" data-mp="j3exk8vsxomf">
                    MYR 20k / 50k
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4743" data-mp="33oqckey9fx02">
                  <div className="h-full bg-amber-400 w-[40%]" data-id="element-4744" data-mp="3hnzsddik0p3i" />
                </div>
                <p className="text-xs text-gray-500 mt-1.5" data-id="element-4745" data-mp="3hlvhucq5gru9">
                  Grow average monthly volume by MYR 30k
                </p>
              </div>
              <div data-id="element-4746" data-mp="3rl47itdzfkat">
                <div className="flex justify-between text-sm mb-2" data-id="element-4747" data-mp="3rkkvftb0jq4p">
                  <span className="font-medium text-gray-700 flex items-center gap-1.5" data-id="element-4748" data-mp="3mnugj9ay30kv">
                    Chargeback rate
                    <CheckCircle2Icon size={14} className="text-green-600" data-id="element-4749" data-mp="561lq9wzgz4k" />
                  </span>
                  <span className="text-green-600 font-medium" data-id="element-4750" data-mp="801vmrqgvs1">
                    0.1% (Excellent)
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4751" data-mp="98g4ku0njyvu">
                  <div className="h-full bg-green-500 w-[100%]" data-id="element-4752" data-mp="96bq5i54nhhb" />
                </div>
                <p className="text-xs text-green-700 mt-1.5" data-id="element-4753" data-mp="srveb2k12dfq">Requirement met</p>
              </div>
            </div>

            {/* Eligibility checklist */}
            <div className="mt-8 pt-8 border-t border-gray-100" data-id="element-4754" data-mp="js8egl6y5fos">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4" data-id="element-4755" data-mp="nuli9424v3ix">
                Eligibility checklist
              </h3>
              <ul className="space-y-3" data-id="element-4756" data-mp="a1vvgxes5ukr">
                {[{
              label: 'Minimum 6 months on platform',
              passed: false
            }, {
              label: 'Minimum MYR 50k average monthly volume',
              passed: false
            }, {
              label: 'Chargeback rate under 1%',
              passed: true
            }, {
              label: 'Minimum 15 active trading days per month',
              passed: true
            }, {
              label: 'No active settlement holds',
              passed: true
            }, {
              label: 'No sustained revenue decline',
              passed: true
            }].map(gate => <li key={gate.label} className="flex items-center gap-3" data-id="element-4757" data-mp="kq352j3jpngw">
                    {gate.passed ? <CheckCircle2Icon size={18} className="text-green-600 flex-shrink-0" data-id="element-4758" data-mp="18fxk97bc7o4b" /> : <span className="w-[18px] h-[18px] rounded-full border-2 border-gray-300 flex-shrink-0" data-id="element-4759" data-mp="ylnm18zprvlo" />}
                    <span className={`text-sm ${gate.passed ? 'text-gray-700' : 'text-gray-500'}`} data-id="element-4760" data-mp="1rbvz4mfe7pte">
                      {gate.label}
                    </span>
                  </li>)}
              </ul>

              {/* Estimated eligibility */}
              <div className="mt-6 flex items-start gap-3 rounded-lg bg-gray-50 border border-gray-100 p-4" data-id="element-4761" data-mp="21h5nal7hc4hg">
                <InfoIcon size={18} className="text-gray-400 flex-shrink-0 mt-0.5" data-id="element-4762" data-mp="2f6pjt9agw5ib" />
                <div data-id="element-4763" data-mp="2aar2g5nk3nre">
                  <div className="text-sm font-semibold text-gray-900" data-id="element-4764" data-mp="1mj3jqb0g7j07">
                    Estimated eligibility
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5" data-id="element-4765" data-mp="1iijmz8jsehel">
                    At your current growth, you may qualify around{' '}
                    <span className="font-medium text-gray-900" data-id="element-4766" data-mp="1wf6ir80071ix">
                      August 2026
                    </span>
                    . This is an estimate, not a guarantee — closest gate: 2
                    more months on platform.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {viewState === 'pre-approved' && <div data-id="element-4767" data-mp="3b03yjk6qhso6">
          {/* Supporting caption above the hero offer */}
          <div className="text-center mb-6" data-id="element-4768" data-mp="3ey1owqc8qkt5">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4769" data-mp="3jypoi0ejc9yz">
              Pre-Qualified Financing Offer
            </h2>
            <p className="text-gray-600" data-id="element-4770" data-mp="3oy2cwpl21p3o">
              A personalised, fully-priced Shariah-compliant offer — no
              application form, accept in one tap.
            </p>
          </div>

          {/* Hero Card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden relative" data-id="element-4771" data-mp="2w9oc622l0eof">
            <div className="absolute top-0 left-0 w-full h-1 bg-[var(--brand-primary)]" data-id="element-4772" data-mp="316wwo9b7cusu" />
            <div className="p-8" data-id="element-4773" data-mp="360j0rd3avqd1">
              <div className="flex items-center justify-between gap-3 mb-8 pb-5 border-b border-gray-100" data-id="element-4774" data-mp="360raiw44mfk0">
                <div className="flex items-center gap-3" data-id="element-4775" data-mp="2dinf5jl0ut7i">
                  <img src={BRAND.logoUrl} alt={BRAND.name} className="h-7 object-contain" data-id="element-4776" data-mp="2dh2lrxag1gb8" />
                  <span className="text-lg font-semibold text-gray-900 leading-none" data-id="element-4777" data-mp="20zrpt95az05y">
                    Murabaha financing
                  </span>
                </div>
                <span className="px-2.5 py-1 bg-red-50 text-[var(--brand-primary)] text-xs font-bold tracking-wide rounded-full whitespace-nowrap" data-id="element-4778" data-mp="1r23xepv664qy">
                  Pre-approved
                </span>
              </div>

              <div className="mb-2 text-sm font-bold tracking-wider text-gray-500 uppercase" data-id="element-4779" data-mp="1i9a1rhvf0sqx">
                YOU'RE PRE-APPROVED
              </div>
              <div className="text-gray-600 mb-1" data-id="element-4780" data-mp="1d8cnvhhfukv8">Financing up to</div>
              <div className="text-4xl font-bold text-gray-900 mb-8" data-id="element-4781" data-mp="18cousqzu57kg">
                {formatMYR(MAX_AMOUNT)}
              </div>

              <div className="mb-8" data-id="element-4782" data-mp="13edqevsb7p9m">
                <div className="flex justify-between text-sm font-medium mb-4" data-id="element-4783" data-mp="za5zcrydpx88">
                  <span className="text-gray-700" data-id="element-4784" data-mp="1o1lmlddpc5kb">Choose amount</span>
                  <span className="text-[var(--brand-primary)] text-lg" data-id="element-4785" data-mp="1o3evkzbsf1vh">
                    {formatMYR(borrowAmount)}
                  </span>
                </div>
                <input type="range" min={MIN_AMOUNT} max={MAX_AMOUNT} step={1000} value={borrowAmount} onChange={e => setBorrowAmount(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--brand-primary)]" data-id="element-4786" data-mp="1f7ywo3w6dwj1" />
                <div className="flex justify-between text-xs text-gray-400 mt-2" data-id="element-4787" data-mp="wbql6ickkpfa">
                  <span data-id="element-4788" data-mp="10csl71eb95ib">{formatMYR(MIN_AMOUNT)}</span>
                  <span data-id="element-4789" data-mp="10dkgsc7wr38l">{formatMYR(MAX_AMOUNT)}</span>
                </div>
              </div>

              {/* How your rate is set — expandable two-layer pricing */}
              <div className="mb-6 rounded-xl border border-gray-200 overflow-hidden" data-id="element-4790" data-mp="2up8s84exkno8">
                <button onClick={() => setRateOpen(o => !o)} aria-expanded={rateOpen} className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors" data-id="element-4791" data-mp="j385xg2k8njq">
                  <span className="text-sm font-semibold text-gray-700" data-id="element-4792" data-mp="o19ps37t8qw9">
                    How your rate is set
                  </span>
                  <ChevronDownIcon size={16} className={`text-gray-400 transition-transform ${rateOpen ? 'rotate-180' : ''}`} data-id="element-4793" data-mp="12ol3aanjykz0" />
                </button>
                {rateOpen && <div className="px-4 pb-4 pt-1 space-y-2 border-t border-gray-100" data-id="element-4794" data-mp="16s9d6zznpsnf">
                    <div className="flex justify-between text-sm" data-id="element-4795" data-mp="3dih8pd5u2te5">
                      <span className="text-gray-600" data-id="element-4796" data-mp="3dipiuxaknkln">
                        Base profit rate (credit score band B)
                      </span>
                      <span className="font-medium text-gray-900" data-id="element-4797" data-mp="1cgrebqc37fbz">
                        ×{BASE_PROFIT_RATE.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-100" data-id="element-4798" data-mp="1mefa8jlfisvq">
                      <span className="font-semibold text-gray-900" data-id="element-4799" data-mp="1mgryfehs5zh8">
                        Your rate
                      </span>
                      <span className="font-bold text-[var(--brand-primary)]" data-id="element-4800" data-mp="1vfvmkwvt152l">
                        ×{PROFIT_RATE.toFixed(2)}
                      </span>
                    </div>
                  </div>}
              </div>

              {/* Murabaha fixed-terms disclosure (updates with slider) */}
              <div className="mb-6 rounded-xl bg-gray-50 border border-gray-100 p-5 space-y-3" data-id="element-4801" data-mp="2a2orr9bm0uh2">
                <div className="flex justify-between text-sm" data-id="element-4802" data-mp="2e6wifc1mokhx">
                  <span className="text-gray-600" data-id="element-4803" data-mp="2j4esx6van7s1">
                    Financing amount you receive
                  </span>
                  <span className="font-medium text-gray-900" data-id="element-4804" data-mp="7ie0s1w8sdfm">
                    {formatMYR(borrowAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-4805" data-mp="2kkvdb2rjnge">
                  <span className="text-gray-600" data-id="element-4806" data-mp="3umkfzlh1pluv">
                    Fixed profit (locked at acceptance)
                  </span>
                  <span className="font-medium text-gray-900" data-id="element-4807" data-mp="3ppajxmz5iby2">
                    {formatMYR(fixedProfit)}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-3 border-t border-gray-200" data-id="element-4808" data-mp="m7ik5merwnpb">
                  <span className="font-semibold text-gray-900" data-id="element-4809" data-mp="heh2x7r3gc2k">
                    Total amount payable
                  </span>
                  <span className="font-bold text-gray-900" data-id="element-4810" data-mp="cg5tuz8mi5le">
                    {formatMYR(totalPayable)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-4811" data-mp="154ti93oo5b36">
                  <span className="text-gray-600" data-id="element-4812" data-mp="1538mxbuj5o49">
                    Estimated daily deduction
                  </span>
                  <span className="font-medium text-gray-900" data-id="element-4813" data-mp="i7yuheszuzaz">
                    {formatRoughDaily(estimatedDailyDeduction)} (
                    {HOLDBACK_PERCENT}% of daily settlement)
                  </span>
                </div>
                <p className="text-xs text-gray-500 pt-1" data-id="element-4814" data-mp="8j74vdbeor38">
                  Your total payable and profit are fixed at acceptance and will
                  not change. The daily deduction amount varies with your sales.
                </p>
              </div>

              {/* Pre-acceptance terms (visual only) */}
              <label className="flex items-start gap-2 mb-4 cursor-pointer" data-id="element-4815" data-mp="n71yqx15h1hw">
                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]" data-id="element-4816" data-mp="r7lsdqmilmza" />
                <span className="text-xs text-gray-600" data-id="element-4817" data-mp="3c0parf7mbo10">
                  I agree to the murabaha financing terms and the fixed total
                  payable shown above.
                </span>
              </label>

              <button onClick={() => navigate('/bills/financing/apply')} className="w-full py-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold text-lg transition-colors shadow-md shadow-red-500/20" data-id="element-4818" data-mp="2zimmpq839sa7">
                Accept Offer &amp; Get Funded
              </button>
              <p className="text-center text-xs text-gray-500 mt-4" data-id="element-4819" data-mp="2grdc1dml0xh1">
                Funds arrive within 24 hours · Profit fixed at acceptance
              </p>
            </div>
          </div>

          {/* Compact benefit strip beneath the offer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6" data-id="element-4820" data-mp="22ynpn8355qje">
            {['Offers refresh every 30 days', 'Profit & total payable fixed at acceptance', 'Funds in your settlement account within 24h', 'Repayment automatic — nothing to remember'].map((item, i) => <div key={i} className="flex items-start gap-2.5" data-id="element-4821" data-mp="3b8vakhpwwwsf" data-mpi={i}>
                <CheckCircle2Icon className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0" size={18} data-id="element-4822" data-mp="3oyqh791ab72z" />
                <span className="text-sm text-gray-600" data-id="element-4823" data-mp="3k6ytr7dfdhh2">{item}</span>
              </div>)}
          </div>

          {/* Secondary accordion: loyalty discounts on future financing */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-4824" data-mp="3txtlgr9fx99w">
            <button onClick={() => setLoyaltyOpen(o => !o)} aria-expanded={loyaltyOpen} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors" data-id="element-4825" data-mp="brx3dntx9biw">
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-700" data-id="element-4826" data-mp="29izysq8w2avc">
                <AwardIcon size={18} className="text-gray-400" data-id="element-4827" data-mp="1zdrq2tt75a4y" />
                Loyalty discounts on future financing
              </span>
              <ChevronDownIcon size={18} className={`text-gray-400 transition-transform ${loyaltyOpen ? 'rotate-180' : ''}`} data-id="element-4828" data-mp="23fdar1bz2spp" />
            </button>
            {loyaltyOpen && <div className="px-5 pb-5 pt-1 border-t border-gray-100" data-id="element-4829" data-mp="1ul89rp34k0wg">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3" data-id="element-4830" data-mp="1for2b8y236f6">
                  {[{
              tier: 'Bronze',
              discount: '−0.00'
            }, {
              tier: 'Silver',
              discount: '−0.03'
            }, {
              tier: 'Gold',
              discount: '−0.06'
            }, {
              tier: 'Platinum',
              discount: '−0.10'
            }].map(t => <div key={t.tier} className="p-4 rounded-lg border border-gray-200 bg-white" data-id="element-4831" data-mp="33lqi4fl5m5h1">
                      <span className="text-xs font-bold text-gray-500" data-id="element-4832" data-mp="2unwwpzoxgw8n">
                        {t.tier}
                      </span>
                      <div className="text-lg font-bold text-gray-900 mt-1.5" data-id="element-4833" data-mp="2pqmwdp003061">
                        {t.discount}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5" data-id="element-4834" data-mp="3ie0nv7kxj3ir">
                        Fixed-profit rate
                      </div>
                    </div>)}
                </div>
                <p className="text-sm text-gray-500 mt-4" data-id="element-4835" data-mp="38m4mnnnjqxab">
                  Earned on your next facility, based on points from this one.
                </p>
              </div>}
          </div>

          {/* How you earn points — forward-looking earning rate table */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6 sm:p-8" data-id="element-4836" data-mp="cidmlwknx4xk">
            <PointsEarningTable data-id="element-4837" data-mp="hg5dqodhgn0p" />
          </div>
        </div>}

      {/* TOP-UP UNLOCKED: re-modelled offer that settles + replaces the current facility */}
      {viewState === 'top-up-unlocked' && currentFacility && <div className="space-y-8" data-id="element-4838" data-mp="rfmhv1d36suk">
          {/* Context banner */}
          <div className="rounded-xl border border-[var(--brand-primary)] bg-red-50/40 p-6 flex items-start gap-4" data-id="element-4839" data-mp="vgzp9fvtv43l">
            <div className="w-10 h-10 rounded-full bg-white border border-red-100 flex items-center justify-center flex-shrink-0 text-[var(--brand-primary)]" data-id="element-4840" data-mp="10erj4fg190af">
              <UnlockIcon size={20} data-id="element-4841" data-mp="1a5cnkjie0z19" />
            </div>
            <div data-id="element-4842" data-mp="1a41a1rz54cwy">
              <div className="text-sm font-bold tracking-wider text-[var(--brand-primary)] uppercase mb-1" data-id="element-4843" data-mp="1a4kogypl0j68">
                Top-up unlocked
              </div>
              <p className="text-gray-700" data-id="element-4844" data-mp="1j598ca1npdr1">
                You've repaid {currentFacility.repaidPercent}% of{' '}
                {currentFacility.id}. Based on your last 90 days, you qualify
                for a larger facility. Your score improved 81.7 → 84.2.
              </p>
            </div>
          </div>

          {/* Offer card */}
          <div className="w-full" data-id="element-4845" data-mp="2ppbl9w7ih9yk">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden relative" data-id="element-4846" data-mp="2ll3uzulenly8">
              <div className="absolute top-0 left-0 w-full h-1 bg-[var(--brand-primary)]" data-id="element-4847" data-mp="1xv9jphgqi5g3" />
              <div className="p-8" data-id="element-4848" data-mp="1sxhr0l89mfau">
                <div className="flex items-center justify-between gap-3 mb-8 pb-5 border-b border-gray-100" data-id="element-4849" data-mp="1syj3mu4xj9uo">
                  <div className="flex items-center gap-3" data-id="element-4850" data-mp="26y0xvp0zvfbf">
                    <img src={BRAND.logoUrl} alt={BRAND.name} className="h-7 object-contain" data-id="element-4851" data-mp="21xd794goh0hq" />
                    <span className="text-lg font-semibold text-gray-900 leading-none" data-id="element-4852" data-mp="3dd1ttuq0rxi0">
                      Murabaha financing
                    </span>
                  </div>
                  <span className="px-2.5 py-1 bg-red-50 text-[var(--brand-primary)] text-xs font-bold tracking-wide rounded-full whitespace-nowrap" data-id="element-4853" data-mp="3lw6k7e62643y">
                    Re-modelled
                  </span>
                </div>

                <div className="mb-2 text-sm font-bold tracking-wider text-gray-500 uppercase" data-id="element-4854" data-mp="3gu7gu4abj770">
                  Your top-up offer
                </div>
                <div className="text-gray-600 mb-1" data-id="element-4855" data-mp="8h0i0hwldrlw">New facility up to</div>
                <div className="text-4xl font-bold text-gray-900 mb-8" data-id="element-4856" data-mp="dcpcw7xo2kbv">
                  {formatMYR(TOPUP_MAX)}
                </div>

                {/* Amount slider anchored to the re-modelled ceiling */}
                <div className="mb-8" data-id="element-4857" data-mp="3mcrkdi12n4o">
                  <div className="flex justify-between text-sm font-medium mb-4" data-id="element-4858" data-mp="re1pc9dmc3t0">
                    <span className="text-gray-700" data-id="element-4859" data-mp="rbynms33sm8g">Choose amount</span>
                    <span className="text-[var(--brand-primary)] text-lg" data-id="element-4860" data-mp="w630tt2ypqhz">
                      {formatMYR(topUpAmount)}
                    </span>
                  </div>
                  <input type="range" min={TOPUP_MIN} max={TOPUP_MAX} step={1000} value={topUpAmount} onChange={e => setTopUpAmount(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--brand-primary)]" data-id="element-4861" data-mp="112fdtx5ua0fl" />
                  <div className="flex justify-between text-xs text-gray-400 mt-2" data-id="element-4862" data-mp="i9bdjsy48pc0">
                    <span data-id="element-4863" data-mp="dcl3bm8zsnyi">{formatMYR(TOPUP_MIN)}</span>
                    <span data-id="element-4864" data-mp="8d02f6a9i5j5">{formatMYR(TOPUP_MAX)}</span>
                  </div>
                </div>

                {/* How your rate is set — three layers resolving live */}
                <div className="mb-6 rounded-xl border border-gray-200 overflow-hidden" data-id="element-4865" data-mp="4d7o0cy6t55v">
                  <button onClick={() => setTopUpRateOpen(o => !o)} aria-expanded={topUpRateOpen} className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors" data-id="element-4866" data-mp="3vi7r4pwfnse0">
                    <span className="text-sm font-semibold text-gray-700" data-id="element-4867" data-mp="2k0v46tf4x5kc">
                      How your rate is set
                    </span>
                    <ChevronDownIcon size={16} className={`text-gray-400 transition-transform ${topUpRateOpen ? 'rotate-180' : ''}`} data-id="element-4868" data-mp="1s95qf8gsr5mu" />
                  </button>
                  {topUpRateOpen && <div className="px-4 pb-4 pt-1 space-y-2 border-t border-gray-100" data-id="element-4869" data-mp="217hizg184dhx">
                      <div className="flex justify-between text-sm" data-id="element-4870" data-mp="261lr42m86rkl">
                        <span className="text-gray-600" data-id="element-4871" data-mp="18jvgyc7n9gx6">
                          Base profit rate (credit score band B)
                        </span>
                        <span className="font-medium text-gray-900" data-id="element-4872" data-mp="6wnrvuklh0uj">
                          ×{TOPUP_BASE_RATE.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-4873" data-mp="1zp0vkw89a0k">
                        <span className="text-gray-600" data-id="element-4874" data-mp="fy53p0qyygdf">
                          Gold loyalty discount (earned on your last facility)
                        </span>
                        <span className="font-medium text-green-700" data-id="element-4875" data-mp="pv011gbiaaq4">
                          −{TOPUP_DISCOUNT.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t border-gray-100" data-id="element-4876" data-mp="zlctrvth5a7c">
                        <span className="font-semibold text-gray-900" data-id="element-4877" data-mp="um9t0nkj1q56">
                          Your rate
                        </span>
                        <span className="font-bold text-[var(--brand-primary)]" data-id="element-4878" data-mp="18n2lz3v6kd66">
                          ×{TOPUP_RATE.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 pt-1" data-id="element-4879" data-mp="1n8ge6vws30gp">
                        Your Gold tier discount is applied because you
                        maintained healthy business signals during your current
                        facility.
                      </p>
                    </div>}
                </div>

                {/* Settlement + new-facility breakdown (recomputes with slider) */}
                <div className="mb-6 rounded-xl bg-gray-50 border border-gray-100 p-5 space-y-3" data-id="element-4880" data-mp="14gnq9fwkg5fu">
                  <div className="flex justify-between text-sm" data-id="element-4881" data-mp="19ajhlzibqu21">
                    <span className="text-gray-600" data-id="element-4882" data-mp="uku2t1hb6rja">New facility amount</span>
                    <span className="font-medium text-gray-900" data-id="element-4883" data-mp="zivwegcd2990">
                      {formatMYR(topUpAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm gap-4" data-id="element-4884" data-mp="lk5y4xdt23j2">
                    <span className="text-gray-600" data-id="element-4885" data-mp="qhfoqgcxkl8h">
                      Less: settle current facility ({currentFacility.id}){' '}
                      <span className="text-gray-400" data-id="element-4886" data-mp="39nwv8xgpp5lt">
                        (incl. ibra' rebate −{formatMYR(activeIbra)})
                      </span>
                    </span>
                    <span className="font-medium text-gray-900 whitespace-nowrap" data-id="element-4887" data-mp="3ikxfcrwr0hjg">
                      −{formatMYR(currentSettlement)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-3 border-t border-gray-200" data-id="element-4888" data-mp="3sjwoeblfef6z">
                    <span className="font-semibold text-gray-900" data-id="element-4889" data-mp="fu7izfedrfc">
                      Net cash you receive
                    </span>
                    <span className="font-bold text-[var(--brand-primary)]" data-id="element-4890" data-mp="59ovwg5y6ukr">
                      {formatMYR(topUpNetCash)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-3 border-t border-gray-200" data-id="element-4891" data-mp="f7cszcr10e63">
                    <span className="text-gray-600" data-id="element-4892" data-mp="k5ebnwkjibh1">
                      New fixed profit (locked at acceptance)
                    </span>
                    <span className="font-medium text-gray-900" data-id="element-4893" data-mp="1znu2riaix5w3">
                      {formatMYR(topUpNewProfit)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-4894" data-mp="1up9aq65fdsiq">
                    <span className="font-semibold text-gray-900" data-id="element-4895" data-mp="1pqg1ipx68nod">
                      New total payable
                    </span>
                    <span className="font-bold text-gray-900" data-id="element-4896" data-mp="2ncn3ds23c1x1">
                      {formatMYR(topUpTotalPayable)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-4897" data-mp="2dmtuwzx8w2x0">
                    <span className="text-gray-600" data-id="element-4898" data-mp="28oigsczx368z">
                      Estimated daily deduction
                    </span>
                    <span className="font-medium text-gray-900" data-id="element-4899" data-mp="364639q0tizuu">
                      {formatRoughDaily(topUpDailyDeduction)} (
                      {HOLDBACK_PERCENT}% of daily settlement)
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 pt-1 font-medium" data-id="element-4900" data-mp="zgfhlrsxysjc">
                    Your current facility is settled and replaced by this new
                    one. You do not hold two facilities at once.
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-4901" data-mp="uons7kliusus">
                    Your total payable and profit are fixed at acceptance and
                    will not change. The daily deduction amount varies with your
                    sales.
                  </p>
                </div>

                {/* Acceptance */}
                <label className="flex items-start gap-2 mb-4 cursor-pointer" data-id="element-4902" data-mp="6rrqhflih8xv">
                  <input type="checkbox" className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]" data-id="element-4903" data-mp="bptfeh5mc2h9" />
                  <span className="text-xs text-gray-600" data-id="element-4904" data-mp="264cgcfaihxx6">
                    I agree to settle my current facility and enter the new
                    murabaha financing on the fixed terms shown above.
                  </span>
                </label>

                <button onClick={() => navigate('/bills/financing/apply')} className="w-full py-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold text-lg transition-colors shadow-md shadow-red-500/20" data-id="element-4905" data-mp="3ll7bc6yj91c9">
                  Accept Top-Up &amp; Get Funded
                </button>
                <p className="text-center text-xs text-gray-500 mt-4" data-id="element-4906" data-mp="87vw0ujwer0x">
                  Your current facility is settled automatically · net funds
                  arrive within 24 hours · profit fixed at acceptance
                </p>
              </div>
            </div>
          </div>

          {/* CIMB Business Tiers — the discount that produced this offer */}
          <BusinessTiers currentTier={currentTier} intro="This offer uses the loyalty discount you earned during your current facility. Maintain your signals on this new facility to reach Platinum on your next top-up." data-id="element-4907" data-mp="2jyq33r1wsuo7" />
        </div>}

      {viewState === 'active-repayment' && <div className="space-y-6" data-id="element-4908" data-mp="1kzffawz6ewfn">
          {/* Current facility — the one live facility, made prominent */}
          {currentFacility && <div className="rounded-xl border-2 border-[var(--brand-primary)] bg-white shadow-sm overflow-hidden" data-id="element-4909" data-mp="sai9vug2cu4b">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-4910" data-mp="x8tgzxez4qiu">
                <div className="flex items-center gap-3" data-id="element-4911" data-mp="11aojh58n59y7">
                  <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-4912" data-mp="1193q3iy2bx1x">
                    Current facility
                  </h2>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700" data-id="element-4913" data-mp="9ipj86ogvkzr">
                    {currentFacility.status}
                  </span>
                </div>
                <button onClick={() => navigate(`/bills/financing/facility/${currentFacility.id}`)} className="inline-flex items-center gap-1 text-sm font-medium text-[var(--brand-primary)] hover:underline" data-id="element-4914" data-mp="611rsd7cqqrr">
                  Manage facility
                  <ChevronRightIcon size={16} data-id="element-4915" data-mp="3j8d0orpn4ui6" />
                </button>
              </div>
              <div className="p-6" data-id="element-4916" data-mp="3ehmtra1kgjld">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-6" data-id="element-4917" data-mp="3eheifml3dmca">
                  <div data-id="element-4918" data-mp="39i1ueyihi984">
                    <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-4919" data-mp="39jcx0v071axn">
                      {currentFacility.id}
                    </div>
                    <div className="text-3xl font-bold text-gray-900" data-id="element-4920" data-mp="30hvkfmlzvrdp">
                      {formatMYR(currentFacility.principal)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1" data-id="element-4921" data-mp="2vk3jy47aawxw">
                      Principal · {formatMYR(currentFacility.totalRepayment)}{' '}
                      total payable
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-sm font-semibold" data-id="element-4922" data-mp="2qrwqhe5x8reh">
                    <TrendingUpIcon size={14} data-id="element-4923" data-mp="2gva8cx4zceg5" />
                    {currentFacility.repaidPercent}% repaid
                  </span>
                </div>

                <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-2" data-id="element-4924" data-mp="34o0gev9ridl5">
                  <div className="h-full bg-[var(--brand-primary)]" style={{
              width: `${currentFacility.repaidPercent}%`
            }} data-id="element-4925" data-mp="39jzdd795p3x6" />
                </div>
                <div className="flex justify-between text-sm text-gray-500 mb-6" data-id="element-4926" data-mp="3nk09yaie1p6u">
                  <span data-id="element-4927" data-mp="3sayszhurh1ds">
                    {formatMYR(currentFacility.outstanding)} outstanding
                  </span>
                  <span data-id="element-4928" data-mp="1gvhrwz93bg40">
                    At your current sales pace, projected to close{' '}
                    {currentFacility.projectedCloseDate}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-gray-100" data-id="element-4929" data-mp="1qmchachmuvto">
                  <div data-id="element-4930" data-mp="1lo16nzjikhae">
                    <div className="text-xs text-gray-500 mb-0.5" data-id="element-4931" data-mp="1lnhz9crhpbam">Holdback</div>
                    <div className="text-sm font-semibold text-gray-900" data-id="element-4932" data-mp="xygld9zw17xy">
                      {currentFacility.holdbackRate}% of daily settlement
                    </div>
                  </div>
                  <div data-id="element-4933" data-mp="16z4ze4pa78au">
                    <div className="text-xs text-gray-500 mb-0.5" data-id="element-4934" data-mp="16zeta3dg5zlt">
                      Fixed profit
                    </div>
                    <div className="text-sm font-semibold text-gray-900" data-id="element-4935" data-mp="2id8trbzs6ygf">
                      {formatMYR(currentFacility.totalRepayment - currentFacility.principal)}
                    </div>
                  </div>
                  <div data-id="element-4936" data-mp="bn5ge8wch2eo">
                    <div className="text-xs text-gray-500 mb-0.5" data-id="element-4937" data-mp="gntgdk2jwtl1">Started</div>
                    <div className="text-sm font-semibold text-gray-900" data-id="element-4938" data-mp="glqmgoya2gb1">
                      {currentFacility.startDate}
                    </div>
                  </div>
                  <div data-id="element-4939" data-mp="qf7cdhvanb9f">
                    <div className="text-xs text-gray-500 mb-0.5" data-id="element-4940" data-mp="qfpf8xcgonqb">
                      Projected close
                    </div>
                    <div className="text-sm font-semibold text-gray-900" data-id="element-4941" data-mp="zdrqe5pwswrb">
                      {currentFacility.projectedCloseDate}
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-4" data-id="element-4942" data-mp="19d0h8pph9v9g">
                  Need more capital?{' '}
                  <button onClick={() => navigate('/bills/financing/apply')} className="font-medium text-[var(--brand-primary)] hover:underline" data-id="element-4943" data-mp="365sewvds0j7k">
                    Request a review
                  </button>
                </p>
              </div>
            </div>}

          {/* Past facilities — muted history, collapsed by default */}
          {pastFacilities.length > 0 && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4944" data-mp="1jeb4htv4dscs">
              <button onClick={() => setPastFacilitiesOpen(o => !o)} aria-expanded={pastFacilitiesOpen} className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors" data-id="element-4945" data-mp="1efrglnwyu6hs">
                <span className="text-sm font-semibold text-gray-700" data-id="element-4946" data-mp="225c5enfsvxyz">
                  Past facilities ({pastFacilities.length})
                </span>
                <ChevronDownIcon size={18} className={`text-gray-400 transition-transform ${pastFacilitiesOpen ? 'rotate-180' : ''}`} data-id="element-4947" data-mp="qs9w8dg16aou" />
              </button>
              {pastFacilitiesOpen && <div className="border-t border-gray-100 divide-y divide-gray-100" data-id="element-4948" data-mp="1o2g5nrbsuaor">
                  {pastFacilities.map(facility => <button key={facility.id} onClick={() => navigate(`/bills/financing/facility/${facility.id}`)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-gray-50 transition-colors group" data-id="element-4949" data-mp="1e5tl73ndxln9">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-4950" data-mp="vfv4y09fom8j">
                        <span className="text-sm font-medium text-gray-500 group-hover:text-[var(--brand-primary)] transition-colors" data-id="element-4951" data-mp="vg4uxnuhate8">
                          {facility.id}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${facility.status === 'Closing soon' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-4952" data-mp="ljs3yxqbitn6">
                          {facility.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-gray-500 whitespace-nowrap" data-id="element-4953" data-mp="3255k4qrz913r">
                        <span data-id="element-4954" data-mp="371vz1as1pqnl">
                          {formatMYR(facility.totalRepayment)} total payable
                        </span>
                        <span className="hidden sm:inline" data-id="element-4955" data-mp="29dlsqjl88gqo">
                          {facility.startDate} – {facility.projectedCloseDate}
                        </span>
                        <ChevronRightIcon size={16} className="text-gray-300" data-id="element-4956" data-mp="2jc1nqxndka5g" />
                      </div>
                    </button>)}
                </div>}
            </div>}

          {/* Top-Up Progress Block */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4957" data-mp="1vniby8549f8m">
            <div className="p-6 border-b border-gray-100" data-id="element-4958" data-mp="1nmu7m213u3ax">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-4959" data-mp="1nl9caa6yugc0">
                Top-Up Progress
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-4960" data-mp="1xd5pkshmid0t">
                When you reach 60% repaid, you can refinance into a larger
                facility that replaces this one.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100" data-id="element-4961" data-mp="14o85cjtuq65f">
              <div className={`p-8 ${viewState === 'top-up-unlocked' ? 'md:col-span-2' : 'md:col-span-3'}`} data-id="element-4962" data-mp="10nobpq8hlko1">
                <div className="flex justify-between items-end mb-4" data-id="element-4963" data-mp="1enz4iytcx9bu">
                  <div data-id="element-4964" data-mp="19mjestg8ien4">
                    <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-4965" data-mp="19m9ot5v6w7hf">
                      Repaid so far
                    </div>
                    <div className="text-3xl font-bold text-gray-900" data-id="element-4966" data-mp="2l300h4idtrif">
                      {formatMYR(101660)}{' '}
                      <span className="text-lg text-gray-400 font-normal" data-id="element-4967" data-mp="6upcwx0lhmdz">
                        / {formatMYR(149500)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-4968" data-mp="3u0gbda29irbs">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-sm font-semibold" data-id="element-4969" data-mp="3u1rf59vpjz2w">
                      <TrendingUpIcon size={14} data-id="element-4970" data-mp="qig1tqh26dw2" />
                      68% repaid
                    </span>
                  </div>
                </div>

                <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-6" data-id="element-4971" data-mp="umnqjnnintua">
                  <div className="h-full bg-[var(--brand-primary)] w-[68%]" data-id="element-4972" data-mp="gsmytduzf73f" />
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-sm text-gray-500" data-id="element-4973" data-mp="lqofxt8ukwc9">
                  <span data-id="element-4974" data-mp="19giv4hgn2wzo">Started · Jan 2026</span>
                  <span data-id="element-4975" data-mp="1e929vhr2by62">
                    At your current sales pace, projected to close Oct 2026
                  </span>
                </div>
              </div>

              {viewState === 'top-up-unlocked' && <div className="p-8 bg-gray-50 flex flex-col justify-center relative overflow-hidden" data-id="element-4976" data-mp="3grze7psqxxnx">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-red-100 rounded-bl-full -mr-8 -mt-8" data-id="element-4977" data-mp="3cs8e2vhby56q" />
                  <div className="flex items-center gap-2 mb-4" data-id="element-4978" data-mp="37tx48kr1buoi">
                    <UnlockIcon size={18} className="text-[var(--brand-primary)]" data-id="element-4979" data-mp="8dgxkzoqp9sp" />
                    <span className="text-sm font-bold tracking-wider text-[var(--brand-primary)] uppercase" data-id="element-4980" data-mp="4dqbh9a5rhui">
                      Top-Up Unlocked
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-3" data-id="element-4981" data-mp="3qqyccefvyawo">
                    {formatMYR(170000)}
                  </div>
                  <p className="text-sm text-gray-600 mb-6" data-id="element-4982" data-mp="narfaqrz7bwv">
                    Re-modelled offer based on your last 90 days. Score improved
                    from 81.7 → 84.2.
                  </p>
                  <button onClick={() => navigate('/bills/financing/apply')} className="w-full py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-900 rounded-lg font-medium transition-colors shadow-sm" data-id="element-4983" data-mp="zk7dajuzuv46">
                    See new offer
                  </button>
                </div>}
            </div>
          </div>
        </div>}

      {/* Business Tiers — airline-status-style programme, shared component. */}
      {viewState === 'active-repayment' && <BusinessTiers currentTier={currentTier} data-id="element-4984" data-mp="1nb3axxm4skjc" />}

      {/* BLOCK 3 (ineligible): Pricing preview — collapsed by default */}
      {viewState === 'ineligible' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4985" data-mp="1wcaxxjj75yy4">
          <button onClick={() => setPricingOpen(o => !o)} aria-expanded={pricingOpen} className="w-full flex items-center justify-between gap-4 p-6 text-left hover:bg-gray-50 transition-colors" data-id="element-4986" data-mp="21aumlrp6dou6">
            <div className="flex items-center gap-2" data-id="element-4987" data-mp="3t9t02z7a1exb">
              <AwardIcon size={20} className="text-gray-400" data-id="element-4988" data-mp="ayent3ygzzey" />
              <span className="text-base font-semibold text-gray-700" data-id="element-4989" data-mp="676cg2dky3z5">
                Preview: how pricing works
              </span>
            </div>
            <ChevronDownIcon size={20} className={`text-gray-400 transition-transform ${pricingOpen ? 'rotate-180' : ''}`} data-id="element-4990" data-mp="ku9cogtaeqq6" />
          </button>

          {pricingOpen && <div className="p-8 pt-2 border-t border-gray-100" data-id="element-4991" data-mp="tuegkgz9v6zl">
              <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-4992" data-mp="oypf283ozg0n">
                How pricing works once you're financing.
              </h2>
              <p className="text-gray-600 mb-8" data-id="element-4993" data-mp="2vpougyu3sq1t">
                These benefits apply once you become eligible and request
                financing.
              </p>

              {/* Two-layer explainer */}
              <div className="space-y-4 mb-10" data-id="element-4994" data-mp="1b5tfldak5b0o">
                <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50" data-id="element-4995" data-mp="1b4i56umlx50l">
                  <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 text-gray-500" data-id="element-4996" data-mp="xdwsjnnqxrb5">
                    <ShieldCheckIcon size={18} data-id="element-4997" data-mp="ngifzz4y1mo4" />
                  </div>
                  <div data-id="element-4998" data-mp="nile76g92m3x">
                    <div className="font-semibold text-gray-900 text-sm" data-id="element-4999" data-mp="sf1xnnci8cao">
                      Layer 1 — Your base rate
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5" data-id="element-5000" data-mp="eh52mvdp8dy4">
                      Set by your business's credit profile when you request
                      financing. Stronger processing history, low chargebacks,
                      and steady growth mean a lower base fixed-profit rate.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50" data-id="element-5001" data-mp="10m35xwk0cz4j">
                  <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 text-[var(--brand-primary)]" data-id="element-5002" data-mp="vnrwvo1jesnd">
                    <AwardIcon size={18} data-id="element-5003" data-mp="lwxcyqbrk553" />
                  </div>
                  <div data-id="element-5004" data-mp="lwnk8uzc3jvp">
                    <div className="font-semibold text-gray-900 text-sm" data-id="element-5005" data-mp="hvc0h6355svf">
                      Layer 2 — Your loyalty discount
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5" data-id="element-5006" data-mp="cvfyfce08bii">
                      Earned over time by maintaining healthy business signals.
                      A higher loyalty tier lowers your base rate further on
                      your next financing or top-up.
                    </p>
                  </div>
                </div>
              </div>

              {/* Discount ladder */}
              <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-6" data-id="element-5007" data-mp="1xddq2czui9os">
                Loyalty discount ladder
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5008" data-mp="29ug9jacrzmje">
                {[{
            tier: 'Bronze',
            discount: 'No discount',
            sub: 'Starting tier'
          }, {
            tier: 'Silver',
            discount: '−0.03 profit rate',
            sub: '400+ points'
          }, {
            tier: 'Gold',
            discount: '−0.06 profit rate',
            sub: '700+ points'
          }, {
            tier: 'Platinum',
            discount: '−0.10 profit rate',
            sub: '1,000+ points'
          }].map(t => <div key={t.tier} className="p-5 rounded-xl border-2 border-gray-200 bg-white" data-id="element-5009" data-mp="3c8nh6lkg4nwk">
                    <span className="font-bold text-gray-500" data-id="element-5010" data-mp="1hlsmvsngkigk">{t.tier}</span>
                    <div className="text-xl font-bold text-gray-900 mt-3" data-id="element-5011" data-mp="1lkrp35ht214b">
                      {t.discount}
                    </div>
                    <div className="text-xs text-gray-500 mt-1" data-id="element-5012" data-mp="17s1zkr32ie2g">{t.sub}</div>
                  </div>)}
              </div>
              <p className="text-sm text-gray-500 mt-4" data-id="element-5013" data-mp="xvfgo7uaxx32">
                Example: a base rate of ×1.15 at Gold tier becomes ×1.09. Your
                actual base rate depends on your credit profile.
              </p>

              {/* How you earn points — forward-looking earning rate table */}
              <PointsEarningTable data-id="element-5014" data-mp="ttbuopa0ba3j" />

              {/* Footer line */}
              <div className="mt-8 p-4 bg-blue-50 rounded-lg flex items-start gap-3 border border-blue-100" data-id="element-5015" data-mp="2j91r3khlcovf">
                <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-5016" data-mp="2o7uyqwa75lno" />
                <p className="text-sm text-blue-800" data-id="element-5017" data-mp="2ycuu6zputf2c">
                  You'll start earning points and unlocking discounts once your
                  financing facility is active.
                </p>
              </div>
            </div>}
        </div>}
    </div>;
}
// Forward-looking points earning-rate table for the no-facility states
// (ineligible / pre-approved). The merchant has not earned points yet, so this
// shows what each behaviour is worth — NOT a balance or activity ledger.
function PointsEarningTable() {
  const monthly = [{
    label: 'On-time settlement, no missed deductions',
    value: '+40',
    note: 'per month'
  }, {
    label: 'Low chargeback rate (under 1%)',
    value: '+30',
    note: '+30 under 0.5% · +10 for 0.5–1%'
  }, {
    label: 'Steady or growing monthly revenue',
    value: '+30',
    note: 'per month'
  }, {
    label: 'A diverse, non-concentrated customer base',
    value: '+20',
    note: 'per month'
  }, {
    label: 'Minimum active trading days',
    value: '+20',
    note: 'per month'
  }];
  const bonuses = [{
    label: '90-day no-missed-settlement streak',
    value: '+200'
  }, {
    label: 'Growth mode (+15% GMV vs prior quarter)',
    value: '+350'
  }, {
    label: 'Zero chargebacks for 60 days',
    value: '+150'
  }];
  return <div data-id="element-5018" data-mp="djryrs65mb3o">
      <div className="flex items-center gap-2 mb-1" data-id="element-5019" data-mp="dk0bnk2a1gev">
        <AwardIcon size={18} className="text-[var(--brand-primary)]" data-id="element-5020" data-mp="ijw6otszxxia" />
        <div className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-5021" data-mp="nbxlonhju2c0">
          How you earn points
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-5" data-id="element-5022" data-mp="s9fri509s1fs">
        Points accumulate automatically from your processing activity once your
        facility is active. Figures below are illustrative.
      </p>

      {/* Monthly earning rates */}
      <div className="rounded-xl border border-gray-200 overflow-hidden" data-id="element-5023" data-mp="1192q6j1m6t55">
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200" data-id="element-5024" data-mp="2lusrjnc2y8ch">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-5025" data-mp="2htz3mtyn0tjl">
            Monthly behaviour
          </span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-5026" data-mp="27tyj54p187dt">
            Points
          </span>
        </div>
        <ul className="divide-y divide-gray-100" data-id="element-5027" data-mp="35g5mkb9lntol">
          {monthly.map(row => <li key={row.label} className="flex items-start justify-between gap-4 px-4 py-3" data-id="element-5028" data-mp="30jf6vp1pj03p">
              <div className="flex items-start gap-2.5 min-w-0" data-id="element-5029" data-mp="2vpkp4r5nwv79">
                <CheckCircle2Icon size={18} className="text-[var(--brand-primary)] flex-shrink-0 mt-0.5" data-id="element-5030" data-mp="2qrj3byguqhs3" />
                <div className="min-w-0" data-id="element-5031" data-mp="3jh85wg97mpvt">
                  <div className="text-sm text-gray-800" data-id="element-5032" data-mp="3jggc9az6b286">{row.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5" data-id="element-5033" data-mp="33yp0lu01mnu4">{row.note}</div>
                </div>
              </div>
              <span className="text-sm font-bold text-gray-900 whitespace-nowrap tabular-nums" data-id="element-5034" data-mp="381v9l9ejit4a">
                {row.value}
                <span className="text-xs font-medium text-gray-400" data-id="element-5035" data-mp="2u2vg9og31q1s">/mo</span>
              </span>
            </li>)}
        </ul>
      </div>

      {/* One-off bonuses */}
      <div className="mt-4" data-id="element-5036" data-mp="3mr12ygqp5p49">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-5037" data-mp="3mrsyjrkanmuj">
          One-off bonuses
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-5038" data-mp="3d06jfk4u53mi">
          {bonuses.map(b => <div key={b.label} className="flex items-center gap-2 rounded-full border border-gray-200 bg-white pl-3 pr-2 py-1.5" data-id="element-5039" data-mp="3hyzwjbg7msm5">
              <span className="text-sm text-gray-700" data-id="element-5040" data-mp="9hvcmxwcibei">{b.label}</span>
              <span className="text-xs font-bold text-[var(--brand-primary)] bg-red-50 rounded-full px-2 py-0.5 whitespace-nowrap tabular-nums" data-id="element-5041" data-mp="eay5f3tatgtn">
                {b.value}
              </span>
            </div>)}
        </div>
      </div>

      {/* Connecting line tying values to tier thresholds */}
      <div className="mt-5 p-4 bg-red-50/60 rounded-lg border border-red-100 flex items-start gap-3" data-id="element-5042" data-mp="27rc1j4bpqvbj">
        <TrendingUpIcon size={20} className="text-[var(--brand-primary)] flex-shrink-0 mt-0.5" data-id="element-5043" data-mp="23sbmzy09qeuc" />
        <p className="text-sm text-gray-700" data-id="element-5044" data-mp="1g3jzhjxmiknw">
          Most healthy merchants earn{' '}
          <span className="font-semibold text-gray-900" data-id="element-5045" data-mp="1b47buwyxh9k9">
            ~130–140 points/month
          </span>
          , plus bonuses — enough to reach{' '}
          <span className="font-semibold text-gray-900" data-id="element-5046" data-mp="1k2rophht4p0k">Silver (400+)</span>{' '}
          within a few months and{' '}
          <span className="font-semibold text-gray-900" data-id="element-5047" data-mp="30fzor5g0vurq">Gold (700+)</span> over
          time.
        </p>
      </div>
    </div>;
}